<script>
import axios from 'axios';
import { useVuelidate } from '@vuelidate/core';
import { required, minValue, integer, maxLength, minLength } from '@vuelidate/validators';

import Datepicker from 'vanillajs-datepicker/Datepicker';
import nl from 'vanillajs-datepicker/locales/nl';
Object.assign(Datepicker.locales, nl);

import mixin from '../../mixin';

export default {
	mixins: [mixin],
	setup() {
		return { v$: useVuelidate() };
	},
	props: {
		idFromUrl: {
			type: String,
			default: null,
		},
		defaultError: {
			type: String,
			default: 'Vul dit veld in.',
		},
		errorIndicator: {
			type: String,
			default: 'Deze verplichte velden moeten worden ingevuld',
		},
	},
	data() {
		return {
			claimDetailsData: {},
			amount: {
				euros: '',
				cents: '',
			},
			date: '',
			paymentReference: '',
			paymentSuccess: false,
			step: 1,
		};
	},
	validations() {
		return {
			amount: {
				euros: { required, minValue: minValue(0) },
				cents: {
					required,
					integer,
					maxLength: maxLength(2),
					minLength: minLength(2)
				}
			},
			date: { required },
		};
	},
	watch: {
		"amount.euros": function (newVal, oldVal) {
			if (oldVal === '' && newVal !== '') {
				if (this.amount.cents === '') this.amount.cents = '00';
			}
			if (newVal === '') this.amount.cents = '';
		},
	},
	async mounted() {
		await this.fetchData(this.idFromUrl);
		if (this.$refs.datepicker) {
			const datepicker = new Datepicker(this.$refs.datepicker, {
				format: 'dd-mm-yyyy',
				autohide: true,
				language: 'nl',
				nextArrow: '<i class="fa-solid fa-caret-right"></i>',
				prevArrow: '<i class="fa-solid fa-caret-left"></i>',
				minDate: new Date(this.claimDetailsData.ingangsdatum),
				maxDate: new Date(),
			});
			if (this.date) datepicker.setDate(this.date);
			this.$refs.datepicker.addEventListener('changeDate', (event) => {
				this.onChangeDate(event);
			});
		}
	},
	methods: {
		async fetchData(idFromURL) {
			try {
				const response = await axios.get(
					`/api/dossiers/${idFromURL}/vorderinggegevens`
				);
				if (response ?? response.data) {
					this.claimDetailsData = response.data;
				} else {
					console.error('Invalid data received:', response);
				}
			} catch (error) {
				console.error('Error fetching data:', error);
			}
		},
		async validatePayment() {
			const result = await this.v$.$validate();
			if (result) {
				if (this.amount.cents && this.amount.cents > 99) this.amount.cents = 99;
				this.step = 2;
			}
		},
		async submitPayment() {
			const result = await this.v$.$validate();
			if (result) {
				const paymentResponse = await axios.post(
					`/api/dossiers/${this.idFromUrl}/payments`,
					{
						DossierNummer: this.idFromUrl.toString(),
						Uw_Referentie: this.claimDetailsData.referentie_nr,
						Bedrag: (parseInt(this.amount.euros + this.amount.cents) / 100).toFixed(2),
						Factuurnummer: '',
						Betalingskenmerk: this.paymentReference,
						Datum: this.date,
					}
				);
				if (paymentResponse.status === 200) {
					this.paymentSuccess = true;
					this.step = 1;
				}
			}
		},
		onChangeDate(event) {
            this.date = this.toUniversalDate(event.detail.date);
		},
		onReload() {
			window.location.reload();
		},
	},
};
</script>

<template>
	<div class="content">
		<template
			v-if="
				claimDetailsData &&
				claimDetailsData.lopend_afgesloten &&
				claimDetailsData.lopend_afgesloten === 'afgesloten'
			"
		>
			<p>
				{{ $store.getters.getLeftText('dossierdetailPaymentImpossible') }}
			</p>
		</template>
		<template v-else>
			<div v-show="step === 1">
				<div v-show="!paymentSuccess">
					<div v-html="$store.getters.getLeftText('dossierdetailPaymentIntro')"></div>
					<div class="columns is-size-10 mb-2">
						<div class="column">
							<p>
								{{ $store.getters.getLeftText('dossierdetailLabelCaseNumber') }}
							</p>
							<p>
								{{ $store.getters.getLeftText('dossierdetailLabelReference') }}
							</p>
						</div>
						<div class="column">
							<p>{{ parseDossierNr(idFromUrl) }}</p>
							<p>{{ claimDetailsData.referentie_nr }}</p>
						</div>
					</div>
				</div>
				<div v-show="paymentSuccess">
					<p>{{ $store.getters.getLeftText('dossierdetailPaymentSuccess') }}</p>
					<hr />
					<div class="is-flex is-justify-content-flex-end">
						<button class="button is-primary is-size-9" @click="onReload">
							{{ $store.getters.getLeftText('dossierdetailButtonEnd') }}
						</button>
					</div>
				</div>
				<div v-show="!paymentSuccess && step === 1">
					<form ref="paymentForm">
						<div class="form-error has-text-danger" v-if="v$.$error">
							{{ errorIndicator }}
						</div>
						<div class="field">
							<label for="payment-date" class="label has-text-weight-normal is-size-9">
								<span class="has-text-danger">*</span>
								{{ $store.getters.getLeftText('dossierdetailLabelPaymentDate') }}
								<span class="has-tooltip-arrow has-tooltip-multiline is-borderless ml-2" :data-tooltip="$store.getters.getLeftText('dossierdetailLabelPaymentDateTooltip')">
									<i class="fa-solid fa-info-circle has-text-grey"></i>
								</span>
							</label>
							<div class="control is-expanded has-icons-right">
								<input
									ref="datepicker"
									class="input"
									id="payment-date"
									type="text"
									required
								>
								<span class="icon is-small is-right">
									<i class="fa-solid fa-calendar-days"></i>
								</span>
							</div>
							<span v-if="v$.date.$error" class="has-text-danger form-error">{{ defaultError }}</span>
						</div>
						<div class="field">
							<label for="amount-euros" class="label has-text-weight-normal is-size-9 mb-0">
								<span class="has-text-danger">*</span>
								{{ $store.getters.getLeftText('dossierdetailLabelAmount') }}
								<span class="has-tooltip-arrow has-tooltip-multiline is-borderless ml-2" :data-tooltip="$store.getters.getLeftText('dossierdetailLabelAmountTooltip')">
												<i class="fa-solid fa-info-circle has-text-grey"></i>
								</span>
							</label>
							<div class="columns is-mobile is-align-items-end my-0">
								<div class="column py-0">
									<div class="field">
										<div class="control">
											<input
												id="amount-euros"
												class="input has-text-right"
												name="amount-euros"
												type="text"
												v-model="amount.euros"
												required
											>
										</div>
									</div>
								</div>
								<div class="column p-0 is-narrow">
									<span>,</span>
								</div>
								<div class="column py-0">
									<div class="field">
										<label
											for="amount-cents"
											class="label has-text-weight-normal is-size-9 is-invisible"
										></label>
										<div class="control">
											<input
												id="amount-cents"
												class="input has-text-right"
												name="amount-cents"
												type="text"
												v-model="amount.cents"
												placeholder="00"
												required
											>
										</div>
									</div>
								</div>
							</div>
							<div class="columns is-mobile is-justify-content-space-between my-0" v-if="v$.amount.euros.$error || v$.amount.cents.$error">
								<div class="column">
									<template v-if="v$.amount.euros.$error">
										<span v-if="v$.amount.euros.$errors[0].$validator === 'required'" class="has-text-danger form-error">{{ defaultError }}</span>
										<span v-else class="has-text-danger form-error">Vul alstublieft een positief bedrag in.</span>
									</template>
								</div>
								<div class="column p-0 is-narrow">
									<span class="has-text-white">,</span>
								</div>
								<div class="column">
									<template v-if="v$.amount.cents.$error">
										<span v-if="v$.amount.cents.$errors[0].$validator === 'required'" class="has-text-danger form-error">{{ defaultError }}</span>
										<span v-if="v$.amount.cents.$errors[0].$validator === 'integer'" class="has-text-danger form-error">Vul de decimalen van het bedrag in het veld hiernaast in.</span>
										<span v-if="v$.amount.cents.$errors[0].$validator === 'maxLength' || v$.amount.cents.$errors[0].$validator === 'minLength'" class="has-text-danger form-error">Decimalen moet bestaan uit 2 cijfers.</span>
									</template>
								</div>
							</div>
						</div>
						<div class="field">
							<label for="paymentReference" class="label has-text-weight-normal is-size-9">
								{{ $store.getters.getLeftText('dossierdetailLabelFeature') }}
								<span class="has-tooltip-arrow has-tooltip-multiline is-borderless ml-2" :data-tooltip="$store.getters.getLeftText('dossierdetailLabelFeatureTooltip')">
									<i class="fa-solid fa-info-circle has-text-grey"></i>
								</span>
							</label>
							<div class="control is-expanded">
								<input
									id="paymentReference"
									class="input"
									name="paymentReference"
									type="text"
									v-model="paymentReference"
								>
							</div>
						</div>
						<hr />
						<div class="buttons is-justify-content-flex-end">
							<button class="button is-primary is-size-9" @click.prevent="validatePayment">
								{{ $store.getters.getLeftText('dossierdetailButtonNext') }}
							</button>
						</div>
					</form>
				</div>
			</div>
			<div v-if="!paymentSuccess && step === 2">
				<div class="columns is-size-10">
					<div class="column">
						<p>
							{{ $store.getters.getLeftText('dossierdetailLabelCaseNumber') }}
						</p>
						<p>
							{{ $store.getters.getLeftText('dossierdetailLabelReference') }}
						</p>
						<p>
							{{ $store.getters.getLeftText('dossierdetailLabelPaymentDate') }}:
						</p>
						<p>
							{{ $store.getters.getLeftText('dossierdetailLabelAmount') }}
						</p>
						<p>
							{{ $store.getters.getLeftText('dossierdetailLabelFeature') }}:
						</p>
					</div>
					<div class="column">
						<p>{{ idFromUrl }}</p>
						<p>{{ claimDetailsData.referentie_nr }}</p>
						<p>{{ toDateStr(date) }}</p>
						<p>
							{{ toEuro((parseInt(amount.euros + amount.cents) / 100).toFixed(2), true) }}
						</p>
						<p>{{ paymentReference }}</p>
					</div>
				</div>
				<hr class="mt-2" />
				<div class="buttons is-justify-content-flex-end">
					<button class="button is-primary is-outlined" @click="step = 1">
						{{ $store.getters.getLeftText('dossierdetailButtonPrevious') }}
					</button>
					<button class="button is-primary is-size-9" @click="submitPayment">
						{{ $store.getters.getLeftText('dossierdetailButtonConfirm') }}
					</button>
				</div>
			</div>
		</template>
	</div>
</template>
